/**
 * Thai phone normalisation. We keep the raw input, an E.164 value when the
 * number is recognisable, and a digits-only national form for partial search.
 * A shared phone number never implies "same person" (teams/families share).
 */
export interface NormalizedPhone {
  raw: string;
  e164: string | null;
  /** National digits, e.g. "0812345678"; used for partial search. */
  national: string | null;
  kind: 'mobile' | 'landline' | 'international' | 'unknown';
}

export function normalizePhone(input: string): NormalizedPhone {
  const raw = input.trim();
  const digits = raw.replace(/[^\d+]/g, '');
  const onlyDigits = digits.replace(/\+/g, '');

  // +66XXXXXXXXX or 66XXXXXXXXX
  let subscriber: string | null = null;
  if (/^\+66\d{8,9}$/.test(digits)) subscriber = digits.slice(3);
  else if (/^66\d{8,9}$/.test(onlyDigits) && !digits.startsWith('0')) subscriber = onlyDigits.slice(2);
  else if (/^0\d{8,9}$/.test(onlyDigits)) subscriber = onlyDigits.slice(1);

  if (subscriber) {
    // Thai mobiles are 9 subscriber digits beginning 6/8/9; landlines 8 digits.
    const isMobile = subscriber.length === 9 && /^[689]/.test(subscriber);
    const isLandline = subscriber.length === 8 && /^[2-7]/.test(subscriber);
    if (isMobile || isLandline) {
      return {
        raw,
        e164: `+66${subscriber}`,
        national: `0${subscriber}`,
        kind: isMobile ? 'mobile' : 'landline',
      };
    }
  }

  if (/^\+\d{8,15}$/.test(digits)) {
    return { raw, e164: digits, national: null, kind: 'international' };
  }
  return { raw, e164: null, national: onlyDigits.length >= 4 ? onlyDigits : null, kind: 'unknown' };
}

/**
 * Thai mobile numbers written inside a chat message ("เบอร์ 098-141-8525", "0981418525", "+66 98 141 8525").
 * Mobiles only (06/08/09 + 8 digits): dates, prices, team sizes and landlines are ignored so a match
 * really points at a phone. Separators may be spaces, "-" or "."; a number glued to more digits is skipped.
 */
export function extractThaiMobiles(text: string): NormalizedPhone[] {
  const out = new Map<string, NormalizedPhone>();
  const re = /(?<![\d+])(?:\+?66[\s.-]?|0)([689])(?:[\s.-]?\d){8}(?!\d)/g;
  for (const m of text.matchAll(re)) {
    const n = normalizePhone(m[0]);
    if (n.kind === 'mobile' && n.e164) out.set(n.e164, n);
  }
  return [...out.values()];
}

/** Digits usable for a partial-match search ("5678", "081-234"). */
export function phoneSearchDigits(query: string): string | null {
  const d = query.replace(/\D/g, '');
  if (d.length < 3) return null;
  // Allow searching "+66 81..." (explicit country code) or a full "66…" number against national storage.
  if (d.startsWith('66') && (query.trim().startsWith('+') || d.length >= 10)) return d.length > 2 ? `0${d.slice(2)}` : null;
  return d;
}

/** Mask for users without the "view full phone" permission: 081-XXX-5678. */
export function maskPhone(national: string | null | undefined): string {
  if (!national) return '';
  if (national.length <= 4) return '•'.repeat(national.length);
  const head = national.slice(0, 3);
  const tail = national.slice(-4);
  return `${head}-XXX-${tail}`;
}
